import React, { useState, useEffect } from 'react';
import { Form, Input, Select, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import supplyChainService, { SellerSKU, Shop } from '../../services/supplyChainService';
import api from '../../services/api';
import { departmentAPI, memberAPI, Department, Member } from '../../services/organizationAPI';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const { Option } = Select;

const SKUMappingEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isNew = !id || id === 'new';

    const [shops, setShops] = useState<Shop[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [internalSkus, setInternalSkus] = useState<any[]>([]);
    const [selectedDeptId, setSelectedDeptId] = useState<number | undefined>();

    const { triggerSave, state: saveState } = useAutoSave<SellerSKU>({
        onSave: async (data) => {
            // Remove helper field
            const saveData = { ...data };
            delete (saveData as any).department_filter;

            if (isNew) {
                const result = await supplyChainService.createSellerSKU(saveData);
                message.success('SKU映射已创建');
                navigate(`/supply-chain/sku-mapping/${result.data.id}`);
            } else {
                await supplyChainService.updateSellerSKU(parseInt(id), saveData);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        fetchReferenceData();
        if (!isNew) {
            fetchSKUMapping();
        } else {
            form.setFieldsValue({ status: 'active' });
        }
    }, [id]);

    const fetchReferenceData = async () => {
        try {
            const [shopRes, deptRes, memberRes, skuRes] = await Promise.all([
                supplyChainService.getShops(),
                departmentAPI.list(),
                memberAPI.list(),
                api.get('/skus/')
            ]);

            const shopData = shopRes.data as any;
            setShops(Array.isArray(shopData) ? shopData : shopData?.results || []);

            const deptData = deptRes.data as any;
            setDepartments(Array.isArray(deptData) ? deptData : deptData?.results || []);

            const memberData = memberRes.data as any;
            setMembers(Array.isArray(memberData) ? memberData : memberData?.results || []);

            const skuData = skuRes.data as any;
            setInternalSkus(Array.isArray(skuData) ? skuData : skuData?.results || []);
        } catch (error) {
            message.error('加载参考数据失败');
        }
    };

    const fetchSKUMapping = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getSellerSKUs();
            const skus = (res.data as any).results || res.data || [];
            const sku = skus.find((s: SellerSKU) => s.id === parseInt(id!));

            if (sku) {
                form.setFieldsValue(sku);

                // Set department filter if owner exists
                if (sku.owner) {
                    const ownerMember = members.find(m => m.user_id === sku.owner);
                    if (ownerMember && ownerMember.department) {
                        setSelectedDeptId(ownerMember.department);
                        form.setFieldsValue({ department_filter: ownerMember.department });
                    }
                }
            } else {
                message.error('SKU映射不存在');
                navigate('/supply-chain/sku-mapping');
            }
        } catch (error) {
            message.error('加载SKU映射失败');
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            if (isNew) {
                delete values.department_filter;
                const result = await supplyChainService.createSellerSKU(values);
                message.success('SKU映射已创建');
                navigate(`/supply-chain/sku-mapping/${result.data.id}`);
            }
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '新增SKU映射' : '编辑SKU映射'}
            breadcrumbs={[
                { title: '供应链', path: '/supply-chain' },
                { title: 'SKU映射', path: '/supply-chain/sku-mapping' },
                { title: isNew ? '新增' : '编辑' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
            >
                {!isNew ? (
                    <>
                        <Form.Item label="店铺">
                            <Input disabled value={form.getFieldValue('shop_name')} />
                        </Form.Item>
                        <Form.Item label="平台SKU">
                            <Input disabled value={form.getFieldValue('sku_code')} />
                        </Form.Item>
                        <Form.Item label="ASIN">
                            <Input disabled value={form.getFieldValue('asin')} />
                        </Form.Item>
                    </>
                ) : (
                    <>
                        <Form.Item name="shop" label="店铺" rules={[{ required: true, message: '请选择店铺' }]}>
                            <Select placeholder="选择店铺">
                                {shops.map(shop => (
                                    <Option key={shop.id} value={shop.id}>
                                        {shop.name} ({shop.platform})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="sku_code" label="平台SKU" rules={[{ required: true, message: '请输入平台SKU' }]}>
                            <Input placeholder="例如: PRODUCT-001" />
                        </Form.Item>

                        <Form.Item name="asin" label="ASIN" rules={[{ required: true, message: '请输入ASIN' }]}>
                            <Input placeholder="例如: B00XXXXXXX" />
                        </Form.Item>
                    </>
                )}

                <Form.Item name="internal_sku" label="内部SKU (映射到)" rules={[{ required: true, message: '请选择内部SKU' }]}>
                    <Select showSearch optionFilterProp="children" placeholder="搜索SKU: SKU - Name">
                        {internalSkus.map(s => (
                            <Option key={s.id} value={s.id}>
                                {s.name} {s.product_name ? `- ${s.product_name}` : ''}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="status" label="状态">
                    <Select>
                        <Option value="active">Active (活跃)</Option>
                        <Option value="inactive">Inactive (不活跃)</Option>
                        <Option value="archived">Archived (归档)</Option>
                    </Select>
                </Form.Item>

                <Form.Item label="负责人部门" name="department_filter">
                    <Select
                        placeholder="筛选部门"
                        allowClear
                        onChange={(val) => {
                            setSelectedDeptId(val);
                            form.setFieldsValue({ owner: undefined });
                        }}
                    >
                        {departments.map(d => (
                            <Option key={d.id} value={d.id}>{d.name}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="owner" label="负责人" rules={[{ required: true, message: '请选择负责人' }]}>
                    <Select
                        placeholder="选择负责人"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                    >
                        {members
                            .filter(m => !selectedDeptId || m.department === selectedDeptId)
                            .map(m => (
                                <Option key={m.id} value={m.user_id}>
                                    {m.first_name || ''}{m.last_name || ''} ({m.username})
                                </Option>
                            ))
                        }
                    </Select>
                </Form.Item>
            </Form>
        </FormPageLayout>
    );
};

export default SKUMappingEdit;
